import { useMemo } from 'react';
import { useGetPromoChannelsQuery } from '../../../../redux/api/fymAPI';
import { SerializedSocialChannel } from '../../promote/types';

type BadgeChannel = Pick<SerializedSocialChannel, 'status' | 'connection' | 'needsReconnect'>;

/**
 * True for a channel that needs a new sign-in. Two rules, and a channel counts once:
 * - the rule of `ChannelStatusMark` ("Needs reconnect"): `disconnected` while the connection is
 *   still known. A disconnect by the artist clears the connection, so it never counts.
 * - `needsReconnect` of the server: the daily read found the ended sign-in, or the sign-in lacks
 *   a scope that the read needs (design social-connectors, "State rules").
 */
export function channelNeedsAttention(channel: BadgeChannel): boolean {
  return channel.needsReconnect || (channel.status === 'disconnected' && channel.connection !== null);
}

/**
 * The Channels attention badge: the count of the channels that need a new sign-in. It reads the
 * Channels screen's own `GET /accounts/:id/promo/channels`. `undefined` until loaded.
 */
export function useChannelsNeedingReconnect(accountId: number | null): number | undefined {
  const { data } = useGetPromoChannelsQuery({ accountId: accountId ?? 0 }, { skip: accountId === null });

  return useMemo(() => {
    if (data === undefined) {
      return undefined;
    }
    return data.channels.filter(channelNeedsAttention).length;
  }, [data]);
}
